import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PageHero } from '@/components/ui/PageHero'
import { QrCode } from '@/components/ui/QrCode'
import { useToast } from '@/contexts/ToastContext'
import { cn } from '@/lib/cn'
import { WALLET_ASSETS } from './assets'
import { DEPOSIT_BANK } from './bank'
import { MethodTabs, type WalletMethod } from './MethodTabs'

/**
 * Deposit flow — Crypto (QR) or Bank transfer.
 */
export function DepositPage({ className }: { className?: string }) {
  const { t } = useTranslation('common')
  const { pushToast } = useToast()
  const [method, setMethod] = useState<WalletMethod>('crypto')

  return (
    <div
      className={cn(
        'terminal-scroll h-full min-h-0 overflow-y-auto',
        className,
      )}
    >
      <PageHero
        tone="bid"
        glow="start"
        eyebrow={t('wallet.depositEyebrow')}
        title={t('wallet.depositTitle')}
        subtitle={t('wallet.depositSubtitle')}
      />

      <div className="mx-auto max-w-5xl space-y-4 p-4 sm:p-6">
        <MethodTabs value={method} onChange={setMethod} />
        {method === 'crypto' ? (
          <CryptoDeposit pushToast={pushToast} />
        ) : (
          <BankDeposit pushToast={pushToast} />
        )}
      </div>
    </div>
  )
}

function CryptoDeposit({
  pushToast,
}: {
  pushToast: (t: { tone?: 'success' | 'danger'; title: string }) => void
}) {
  const { t } = useTranslation('common')
  const [assetId, setAssetId] = useState(WALLET_ASSETS[0].id)
  const asset = WALLET_ASSETS.find((a) => a.id === assetId) ?? WALLET_ASSETS[0]
  const [networkId, setNetworkId] = useState(asset.networks[0].id)
  const network =
    asset.networks.find((n) => n.id === networkId) ?? asset.networks[0]
  const address = asset.addresses[network.id] ?? ''

  const truncated = useMemo(() => {
    if (address.length < 20) return address
    return `${address.slice(0, 10)}…${address.slice(-8)}`
  }, [address])

  const onAsset = (id: string) => {
    setAssetId(id)
    const next = WALLET_ASSETS.find((a) => a.id === id)
    if (next) setNetworkId(next.networks[0].id)
  }

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(address)
      pushToast({ tone: 'success', title: t('wallet.copied') })
    } catch {
      pushToast({ tone: 'danger', title: t('wallet.copyFailed') })
    }
  }

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_1.05fr]">
      <div className="flex flex-col gap-4">
        <section className="rounded-xl border border-terminal-border/40 bg-terminal-surface p-4">
          <h2 className="text-[10px] font-semibold uppercase tracking-wider text-terminal-muted">
            {t('wallet.selectAsset')}
          </h2>
          <div className="mt-2.5 flex flex-wrap gap-1.5">
            {WALLET_ASSETS.map((a) => (
              <button
                key={a.id}
                type="button"
                onClick={() => onAsset(a.id)}
                className={cn(
                  'flex min-w-[4.5rem] flex-col items-start rounded-lg border px-3 py-2 transition-colors',
                  a.id === asset.id
                    ? 'border-accent/50 bg-accent/10 text-accent'
                    : 'border-terminal-border/40 bg-terminal-bg text-terminal-muted hover:text-terminal-fg',
                )}
              >
                <span className="text-xs font-semibold">{a.symbol}</span>
                <span className="text-[10px] opacity-70">{a.name}</span>
              </button>
            ))}
          </div>
        </section>

        <section className="rounded-xl border border-terminal-border/40 bg-terminal-surface p-4">
          <h2 className="text-[10px] font-semibold uppercase tracking-wider text-terminal-muted">
            {t('wallet.selectNetwork')}
          </h2>
          <div className="mt-2.5 flex flex-wrap gap-1.5">
            {asset.networks.map((n) => (
              <button
                key={n.id}
                type="button"
                onClick={() => setNetworkId(n.id)}
                className={cn(
                  'h-9 rounded-lg border px-3 text-xs font-semibold transition-colors',
                  n.id === network.id
                    ? 'border-accent bg-accent text-white'
                    : 'border-terminal-border/40 bg-terminal-bg text-terminal-muted hover:text-terminal-fg',
                )}
              >
                {n.label}
              </button>
            ))}
          </div>
          <ul className="mt-3 space-y-1.5 text-[11px] text-terminal-muted">
            <li className="flex justify-between gap-2">
              <span>{t('wallet.minDeposit')}</span>
              <span className="font-mono tabular-nums text-terminal-fg">
                {network.minDeposit} {asset.symbol}
              </span>
            </li>
            <li className="flex justify-between gap-2">
              <span>{t('wallet.confirmations')}</span>
              <span className="font-mono tabular-nums text-terminal-fg">
                {network.confirmations}
              </span>
            </li>
          </ul>
        </section>

        <section className="rounded-xl border border-accent/25 bg-accent/5 p-4">
          <p className="text-[11px] leading-relaxed text-terminal-muted">
            <span className="font-semibold text-accent">
              {t('wallet.warningTitle')}
            </span>{' '}
            {t('wallet.depositWarning', {
              network: network.label,
              asset: asset.symbol,
            })}
          </p>
        </section>
      </div>

      <div className="flex flex-col items-center rounded-xl border border-terminal-border/40 bg-terminal-surface p-5 sm:p-6">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-terminal-muted">
          {t('wallet.scanToDeposit')}
        </p>
        <p className="mt-1 text-sm font-semibold text-terminal-fg">
          {asset.symbol} · {network.label}
        </p>
        <div className="mt-4">
          <QrCode
            value={address}
            size={188}
            label={t('wallet.depositQrLabel', { asset: asset.symbol })}
          />
        </div>
        <div className="mt-5 w-full max-w-sm">
          <label className="text-[10px] font-semibold uppercase tracking-wider text-terminal-muted">
            {t('wallet.depositAddress')}
          </label>
          <div className="mt-1.5 flex items-stretch gap-1.5">
            <code
              className={cn(
                'min-w-0 flex-1 truncate rounded-lg border border-terminal-border/40',
                'bg-terminal-bg px-3 py-2.5 font-mono text-[11px] text-terminal-fg',
              )}
              title={address}
            >
              <span className="sm:hidden">{truncated}</span>
              <span className="hidden sm:inline">{address}</span>
            </code>
            <button
              type="button"
              onClick={() => void copy()}
              className="shrink-0 rounded-lg bg-accent px-3 text-xs font-semibold text-white hover:bg-accent-muted"
            >
              {t('wallet.copy')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function BankDeposit({
  pushToast,
}: {
  pushToast: (t: { tone?: 'success' | 'danger'; title: string }) => void
}) {
  const { t } = useTranslation('common')
  const bank = DEPOSIT_BANK
  const qrPayload = `BCD\n001\n1\nSCT\n${bank.swift}\n${bank.beneficiary}\n${bank.iban}\n${bank.currency}0\n\n${bank.reference}\n`

  const copyField = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value)
      pushToast({
        tone: 'success',
        title: t('wallet.fieldCopied', { field: label }),
      })
    } catch {
      pushToast({ tone: 'danger', title: t('wallet.copyFailed') })
    }
  }

  const rows: { key: string; label: string; value: string }[] = [
    { key: 'beneficiary', label: t('wallet.bankBeneficiary'), value: bank.beneficiary },
    { key: 'bank', label: t('wallet.bankName'), value: bank.bankName },
    { key: 'iban', label: t('wallet.bankIban'), value: bank.iban },
    { key: 'swift', label: t('wallet.bankSwift'), value: bank.swift },
    { key: 'ref', label: t('wallet.bankReference'), value: bank.reference },
    { key: 'ccy', label: t('wallet.bankCurrency'), value: bank.currency },
  ]

  return (
    <div className="grid gap-4 lg:grid-cols-[1.1fr_0.9fr]">
      <section className="rounded-xl border border-terminal-border/40 bg-terminal-surface p-4 sm:p-5">
        <h2 className="text-[10px] font-semibold uppercase tracking-wider text-terminal-muted">
          {t('wallet.bankDetails')}
        </h2>
        <ul className="mt-3 divide-y divide-terminal-border/40">
          {rows.map((row) => (
            <li
              key={row.key}
              className="flex items-center justify-between gap-3 py-2.5"
            >
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-wider text-terminal-muted">
                  {row.label}
                </p>
                <p className="mt-0.5 truncate font-mono text-xs text-terminal-fg">
                  {row.value}
                </p>
              </div>
              <button
                type="button"
                onClick={() => void copyField(row.value, row.label)}
                className="shrink-0 rounded-md border border-terminal-border/40 px-2 py-1 text-[10px] font-semibold text-accent hover:bg-accent/10"
              >
                {t('wallet.copy')}
              </button>
            </li>
          ))}
        </ul>
        <p className="mt-4 rounded-lg border border-accent/25 bg-accent/5 px-3 py-2.5 text-[11px] leading-relaxed text-terminal-muted">
          <span className="font-semibold text-accent">
            {t('wallet.warningTitle')}
          </span>{' '}
          {t('wallet.bankDepositWarning')}
        </p>
      </section>

      <div className="flex flex-col items-center rounded-xl border border-terminal-border/40 bg-terminal-surface p-5">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-terminal-muted">
          {t('wallet.bankQrTitle')}
        </p>
        <p className="mt-1 text-center text-xs text-terminal-muted">
          {t('wallet.bankQrHint')}
        </p>
        <div className="mt-4">
          <QrCode value={qrPayload} size={168} label={t('wallet.bankQrTitle')} />
        </div>
        <ol className="mt-5 w-full space-y-2 text-[11px] text-terminal-muted">
          {[1, 2, 3].map((n) => (
            <li key={n} className="flex gap-2">
              <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent/15 text-[10px] font-bold text-accent">
                {n}
              </span>
              {t(`wallet.bankDepositStep${n}`)}
            </li>
          ))}
        </ol>
      </div>
    </div>
  )
}
